// Generated by ReScript, PLEASE EDIT WITH CARE

import * as I18n from "../primitives/I18n.res.js";
import * as Json from "../primitives/Json.res.js";
import * as React from "react";
import * as Future from "rescript-future/src/Future.res.js";
import * as Stdlib_Array from "@rescript/runtime/lib/es6/Stdlib_Array.js";
import * as Stdlib_Option from "@rescript/runtime/lib/es6/Stdlib_Option.js";
import * as Stdlib_Result from "@rescript/runtime/lib/es6/Stdlib_Result.js";
import * as Stdlib_Promise from "@rescript/runtime/lib/es6/Stdlib_Promise.js";
import * as Primitive_option from "@rescript/runtime/lib/es6/Primitive_option.js";
import * as JsxRuntime from "react/jsx-runtime";
import * as ReactUpdateDebounced from "../primitives/ReactUpdateDebounced.res.js";
import * as CompanyRequestRegistry from "./CompanyRequestRegistry.res.js";
import * as InputSearchComboBoxField from "../resources/selection-and-input/InputSearchComboBoxField.res.js";

function endpoint(query) {
  return "https://recherche-entreprises.api.gouv.fr/search?q=" + encodeURIComponent(query) + "&etat_administratif=A&per_page=" + (10).toString();
}

function decodeItem(result) {
  let dict = Json.decodeDict(result);
  let corporateName = Stdlib_Option.orElse(Json.flatDecodeDictFieldString(dict, "nom_raison_sociale"), Json.flatDecodeDictFieldString(dict, "nom_complet"));
  if (corporateName === undefined) {
    return;
  }
  let headquarters = Json.flatDecodeDictFieldDict(dict, "siege");
  let director = CompanyRequestRegistry.decodeDirector(dict);
  return {
    corporateName: corporateName,
    siret: Stdlib_Option.getOr(Json.flatDecodeDictFieldString(headquarters, "siret"), ""),
    vatNumber: Stdlib_Option.getOr(Stdlib_Option.flatMap(Stdlib_Option.flatMap(Json.flatDecodeDictFieldArray(dict, "tva"), tvaNumbers => tvaNumbers[0]), Json.decodeString), ""),
    street: CompanyRequestRegistry.decodeStreet(headquarters),
    postalCode: Stdlib_Option.getOr(Json.flatDecodeDictFieldString(headquarters, "code_postal"), ""),
    city: Stdlib_Option.getOr(Json.flatDecodeDictFieldString(headquarters, "libelle_commune"), ""),
    directorFirstName: Stdlib_Option.getOr(Stdlib_Option.map(director, param => param[0]), ""),
    directorLastName: Stdlib_Option.getOr(Stdlib_Option.map(director, param => param[1]), "")
  };
}

function decode(result) {
  let results = Json.flatDecodeDictFieldArray(Json.decodeDict(result), "results");
  if (results !== undefined) {
    return {
      TAG: "Ok",
      _0: Stdlib_Array.filterMap(results, decodeItem)
    };
  } else {
    return {
      TAG: "Error",
      _0: undefined
    };
  }
}

function make(query) {
  let abortController = new AbortController();
  let abortSignal = abortController.signal;
  return Future.make(resolve => {
    Stdlib_Promise.$$catch(fetch(endpoint(query), {
      method: "GET",
      signal: Primitive_option.some(abortSignal)
    }).then(prim => prim.json()).then(json => resolve(decode(json))), param => {
      resolve({
        TAG: "Error",
        _0: undefined
      });
      return Promise.resolve();
    });
    return () => {
      abortController.abort();
    };
  });
}

let CompanyListRequest = {
  endpoint: endpoint,
  decodeItem: decodeItem,
  decode: decode,
  make: make
};

function CompanyComboBoxField(props) {
  let onRequestAutoComplete = props.onRequestAutoComplete;
  let value = props.companyName;
  let __required = props.required;
  let __placeholder = props.placeholder;
  let __label = props.label;
  let label = __label !== undefined ? __label : I18n.t("Company");
  let placeholder = __placeholder !== undefined ? __placeholder : I18n.t("company_combobox.input_placeholder");
  let required = __required !== undefined ? __required : false;
  let match = React.useState(() => []);
  let setSearchResults = match[1];
  let searchResults = match[0];
  let match$1 = React.useState(() => false);
  let setRequestPending = match$1[1];
  let selectedNameRef = React.useRef("");
  let query = value.trim();
  let shouldFetch = query.length >= 3;
  let debouncedQuery = ReactUpdateDebounced.use(query, 300);
  let awaitingDebounce = shouldFetch && debouncedQuery !== query;
  let loading = shouldFetch && (awaitingDebounce || match$1[0]);
  React.useEffect(() => {
    if (debouncedQuery.length < 3) {
      setSearchResults(param => []);
      setRequestPending(param => false);
      return;
    }
    if (debouncedQuery === selectedNameRef.current) {
      return;
    }
    setRequestPending(param => true);
    let future = make(debouncedQuery);
    Future.get(future, result => {
      setRequestPending(param => false);
      setSearchResults(param => Stdlib_Result.getOr(result, []));
    });
    return () => Future.cancel(future);
  }, [debouncedQuery]);
  let items = React.useMemo(() => searchResults.map((company, index) => {
    let match = company.postalCode;
    let match$1 = company.city;
    let location;
    let exit = 0;
    if (match === "" && match$1 === "") {
      location = "";
    } else {
      exit = 1;
    }
    if (exit === 1) {
      location = ` — ` + match + ` ` + match$1;
    }
    let siret = company.siret === "" ? "" : ` (` + company.siret + `)`;
    return {
      key: company.siret === "" ? company.corporateName + index.toString() : company.siret,
      label: company.corporateName + location + siret,
      value: company
    };
  }), [searchResults]);
  let itemTextValue = React.useCallback(item => item.value.corporateName, []);
  let onSelectItem = item => {
    selectedNameRef.current = item.value.corporateName.trim();
    onRequestAutoComplete(item.value);
  };
  let onSelectItem$1 = React.useCallback(onSelectItem, []);
  let showResults = ReactUpdateDebounced.use(shouldFetch, 350);
  return JsxRuntime.jsx(InputSearchComboBoxField.make, {
    label: label,
    required: required,
    loading: loading,
    placeholder: placeholder,
    errorMessage: props.errorMessage,
    noResultLabel: I18n.t("No French company suggestion"),
    showResults: showResults,
    items: items,
    itemTextValue: itemTextValue,
    value: value,
    onInputChange: props.onInputChange,
    onSelectionChange: onSelectItem$1
  });
}

let resultsPerPage = 10;

let queryValueThreshold = 3;

let queryDebounceDelay = 300;

let make$1 = CompanyComboBoxField;

export {
  resultsPerPage,
  CompanyListRequest,
  queryValueThreshold,
  queryDebounceDelay,
  make$1 as make,
}
/* I18n Not a pure module */
